import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Heart, Share2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductFeed = ({ productContext, title }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [skip, setSkip] = useState(0);
  const [adsShown, setAdsShown] = useState(0);
  const observerTarget = useRef(null);

  useEffect(() => {
    fetchPosts(true);
    fetchAds();
  }, [productContext]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchPosts(false);
        }
      },
      { threshold: 0.5 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loading, skip]);

  const fetchPosts = async (reset = false) => {
    setLoading(true);
    try {
      const currentSkip = reset ? 0 : skip;
      const response = await axios.get(`${API}/feed/posts?product_context=${productContext}&skip=${currentSkip}&limit=20`);
      const newPosts = response.data.posts || [];
      
      if (reset) {
        setPosts(newPosts);
        setSkip(20);
      } else {
        setPosts(prev => [...prev, ...newPosts]);
        setSkip(prev => prev + 20);
      }
      
      setHasMore(newPosts.length === 20);
    } catch (error) {
      toast.error('Failed to load feed');
    } finally {
      setLoading(false);
    }
  };

  const fetchAds = async () => {
    try {
      const response = await axios.get(`${API}/feed/ads`);
      setAds(response.data.ads?.filter(ad => ad.product_type === productContext) || []);
    } catch (error) {
      console.error('Failed to load ads');
    }
  };

  const handleLike = async (postId) => {
    if (!user) {
      toast.error('Please login');
      return;
    }
    try {
      await axios.post(`${API}/feed/posts/${postId}/like`);
      setPosts(prev => prev.map(p => p.id === postId ? {...p, like_count: p.like_count + 1} : p));
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <h1 className="text-3xl font-bold mb-6">{title}</h1>
        
        {loading && posts.length === 0 ? (
          <div className="text-center py-20">Loading...</div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">No posts yet</div>
        ) : (
          <div className="space-y-4">
            {posts.map((post, idx) => {
              const shouldShowAd = idx > 0 && idx % 6 === 0 && adsShown < 3 && ads.length > 0;
              const ad = shouldShowAd ? ads[adsShown % ads.length] : null;
              
              return (
                <React.Fragment key={post.id}>
                  {ad && (() => { setAdsShown(prev => prev + 1); return (
                    <Card className="border-2 border-blue-200 dark:border-blue-800">
                      <CardContent className="p-4">
                        <Badge variant="outline" className="text-xs mb-2">Sponsored</Badge>
                        <h3 className="font-bold text-lg mb-1">{ad.title}</h3>
                        <p className="text-sm text-muted-foreground mb-3">{ad.subtitle}</p>
                        <Button size="sm" onClick={() => navigate(ad.cta_url)}>{ad.cta_text}</Button>
                      </CardContent>
                    </Card>
                  ); })()}
                  
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                          {post.author?.avatar_url ? <img src={post.author.avatar_url} className="w-full h-full rounded-full object-cover" alt={post.author?.full_name} /> : post.author?.full_name?.[0] || 'U'}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{post.author?.full_name || 'User'}</span>
                            <Badge variant="outline" className="text-xs">{post.post_type}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">{new Date(post.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                      
                      <p className="text-foreground mb-3 whitespace-pre-wrap">{post.text}</p>
                      
                      {post.media_images && post.media_images.length > 0 && (
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          {post.media_images.slice(0, 4).map((img, imgIdx) => (
                            <img key={imgIdx} src={img} className="w-full h-32 object-cover rounded" alt="" />
                          ))}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-4 pt-3 border-t">
                        <button onClick={() => handleLike(post.id)} className="flex items-center gap-1 text-muted-foreground hover:text-red-500">
                          <Heart className="w-4 h-4" />
                          <span className="text-sm">{post.like_count || 0}</span>
                        </button>
                        <button className="flex items-center gap-1 text-muted-foreground hover:text-blue-500">
                          <Share2 className="w-4 h-4" />
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                </React.Fragment>
              );
            })}
            
            <div ref={observerTarget} className="h-10 flex items-center justify-center">
              {loading && <div className="text-muted-foreground">Loading more...</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductFeed;
